import { useState, useEffect } from 'react';
import type { QuestionContent, ShortAnswerContent } from '@/types';
import styles from './QuestionEditor.module.scss';

interface ShortAnswerEditorProps {
  content: QuestionContent | null;
  onChange: (content: QuestionContent) => void;
}

export function ShortAnswerEditor({ content, onChange }: ShortAnswerEditorProps) {
  const [acceptedAnswers, setAcceptedAnswers] = useState<string[]>(['']);
  const [maxLength, setMaxLength] = useState<number>(200);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [matchingStrategy, setMatchingStrategy] = useState<'exact' | 'fuzzy' | 'keywords'>('exact');
  const [keywords, setKeywords] = useState<string[]>([]);

  // Initialize from existing content
  useEffect(() => {
    if (content && content.type === 'short_answer') {
      setAcceptedAnswers(content.acceptedAnswers);
      setMaxLength(content.maxLength || 200);
      setCaseSensitive(content.caseSensitive || false);
      setMatchingStrategy(content.matchingStrategy);
      setKeywords(content.keywords || []);
    }
  }, [content]);

  // Update parent when data changes
  useEffect(() => {
    if (acceptedAnswers.some((ans) => ans.trim() !== '')) {
      const newContent: ShortAnswerContent = {
        type: 'short_answer',
        acceptedAnswers: acceptedAnswers.filter((ans) => ans.trim() !== ''),
        maxLength,
        caseSensitive,
        matchingStrategy,
        keywords: matchingStrategy === 'keywords' ? keywords : undefined,
      };
      onChange(newContent);
    }
  }, [acceptedAnswers, maxLength, caseSensitive, matchingStrategy, keywords, onChange]);

  const handleAcceptedAnswersChange = (value: string) => {
    setAcceptedAnswers(value.split(',').map((ans) => ans.trim()));
  };

  const handleKeywordsChange = (value: string) => {
    setKeywords(value.split(',').map((kw) => kw.trim()).filter(Boolean));
  };

  return (
    <div className={styles.editor}>
      <h4 className={styles.editor__heading}>Short Answer Configuration</h4>

      <div className={styles.editor__info}>
        Short answer questions allow students to provide brief text responses (1-2 sentences).
      </div>

      <div className={styles.editor__field}>
        <label htmlFor="accepted-answers" className={styles.editor__label}>
          Accepted Answers (comma-separated) *
        </label>
        <input
          id="accepted-answers"
          type="text"
          className={styles.editor__input}
          value={acceptedAnswers.join(', ')}
          onChange={(e) => handleAcceptedAnswersChange(e.target.value)}
          placeholder="e.g., photosynthesis, photosynthetic process"
        />
        <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
          Provide multiple variations of the correct answer
        </p>
      </div>

      <div className={styles.editor__field}>
        <label htmlFor="matching-strategy" className={styles.editor__label}>
          Matching Strategy
        </label>
        <select
          id="matching-strategy"
          className={styles.editor__input}
          value={matchingStrategy}
          onChange={(e) => setMatchingStrategy(e.target.value as 'exact' | 'fuzzy' | 'keywords')}
        >
          <option value="exact">Exact match</option>
          <option value="fuzzy">Fuzzy match (allows minor typos)</option>
          <option value="keywords">Keyword match</option>
        </select>
      </div>

      {matchingStrategy === 'keywords' && (
        <div className={styles.editor__field}>
          <label htmlFor="keywords" className={styles.editor__label}>
            Required Keywords (comma-separated) *
          </label>
          <input
            id="keywords"
            type="text"
            className={styles.editor__input}
            value={keywords.join(', ')}
            onChange={(e) => handleKeywordsChange(e.target.value)}
            placeholder="e.g., light, energy, glucose"
          />
          <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
            Answer must contain all these keywords to be correct
          </p>
        </div>
      )}

      <div className={styles.editor__field}>
        <label htmlFor="max-length" className={styles.editor__label}>
          Maximum Length (characters)
        </label>
        <input
          id="max-length"
          type="number"
          className={styles.editor__input}
          value={maxLength}
          onChange={(e) => setMaxLength(parseInt(e.target.value, 10))}
          min={50}
          max={500}
        />
      </div>

      <div className={styles.editor__field}>
        <label className={styles.editor__checkboxLabel}>
          <input
            type="checkbox"
            className={styles.editor__checkbox}
            checked={caseSensitive}
            onChange={(e) => setCaseSensitive(e.target.checked)}
          />
          <span className={styles.editor__checkboxText}>
            Case sensitive matching
          </span>
        </label>
      </div>
    </div>
  );
}
